// ✨ components/common/InteractiveField.js - ФИНАЛЬНАЯ, УПРОЩЕННАЯ И РАБОЧАЯ ВЕРСИЯ
import React, { useState, useEffect, useRef } from 'react';

const InteractiveField = ({ value, onChange, placeholder = "", type = "text", className = "", isRequired = false, fullWidth = false, style = {}, showValidation = false, integerOnly = false, validationFieldId }) => {
    const [localValue, setLocalValue] = useState(value);
    const [inputWidth, setInputWidth] = useState(0);
    const spanRef = useRef(null);

    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    useEffect(() => {
        if (spanRef.current) {
            // Небольшой запас по ширине, чтобы текст не обрезался
            const isMobile = window.matchMedia('(max-width: 768px)').matches;
            const extraWidth = isMobile ? 10 : 12;
            const width = spanRef.current.offsetWidth + extraWidth;
            setInputWidth(width);
        }
    }, [localValue, placeholder]);

    // Обработчик нажатия клавиш - блокируем недопустимые символы для масок и integerOnly
    const handleKeyDown = (e) => {
        const hasDeptMask = className && className.includes('dept-code-mask');
        const hasPassportSerialMask = className && className.includes('passport-serial-mask');
        const hasPassportNumberMask = className && className.includes('passport-number-mask');
        const hasPhoneMask = className && className.includes('phone-mask');
        const hasNumericMask = hasDeptMask || hasPassportSerialMask || hasPassportNumberMask || hasPhoneMask;

        if (type === 'number' && integerOnly) {
            // Разрешаем: цифры, Backspace, Delete, Tab, Escape, Enter, стрелки
            const allowedKeys = [
                'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
                'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
                'Home', 'End'
            ];
            
            // Разрешаем Ctrl/Cmd комбинации (Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X)
            if (e.ctrlKey || e.metaKey) {
                return;
            }
            
            // Если это разрешенная клавиша - пропускаем
            if (allowedKeys.includes(e.key)) {
                return;
            }
            
            // Если это цифра - пропускаем
            if (/^\d$/.test(e.key)) {
                return;
            }
            
            // Все остальное блокируем (точки, запятые, минусы, буквы и т.д.)
            e.preventDefault();
        }

        // Маски, допускающие только цифры и служебные клавиши
        if (hasNumericMask) {
            const allowedKeys = [
                'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
                'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
                'Home', 'End'
            ];

            if (e.ctrlKey || e.metaKey) {
                return;
            }

            if (allowedKeys.includes(e.key)) {
                return;
            }

            if (/^\d$/.test(e.key)) {
                return;
            }

            e.preventDefault();
        }
    };

    // Обработчик вставки текста - очищаем от недопустимых символов
    const handlePaste = (e) => {
        const hasDeptMask = className && className.includes('dept-code-mask');
        const hasPassportSerialMask = className && className.includes('passport-serial-mask');
        const hasPassportNumberMask = className && className.includes('passport-number-mask');
        const hasPhoneMask = className && className.includes('phone-mask');

        if (type === 'number' && integerOnly) {
            e.preventDefault();
            const pastedText = (e.clipboardData || window.clipboardData).getData('text');
            // Оставляем только цифры
            const cleaned = pastedText.replace(/[^\d]/g, '');
            if (cleaned) {
                const numValue = parseInt(cleaned, 10);
                if (!isNaN(numValue)) {
                    setLocalValue(numValue.toString());
                }
            }
        } else if (hasDeptMask || hasPassportSerialMask || hasPassportNumberMask || hasPhoneMask) {
            // Для масок оставляем только цифры и применяем форматирование
            e.preventDefault();
            const pastedText = (e.clipboardData || window.clipboardData).getData('text');
            let digits = pastedText.replace(/\D/g, '');

            if (hasDeptMask) {
                digits = digits.slice(0, 6);
                if (digits) {
                    const part1 = digits.slice(0, 3);
                    const part2 = digits.slice(3, 6);
                    const masked = part2 ? `${part1}-${part2}` : part1;
                    setLocalValue(masked);
                }
            } else if (hasPassportSerialMask) {
                digits = digits.slice(0, 4);
                setLocalValue(digits);
            } else if (hasPassportNumberMask) {
                digits = digits.slice(0, 6);
                setLocalValue(digits);
            } else if (hasPhoneMask) {
                // Нормализуем телефон до формата +7 (XXX) XXX-XX-XX
                if (!digits) {
                    setLocalValue('');
                    return;
                }
                if (digits.startsWith('8')) {
                    digits = '7' + digits.slice(1);
                } else if (!digits.startsWith('7')) {
                    digits = '7' + digits;
                }
                digits = digits.slice(0, 11);

                const p1 = digits.slice(1, 4);
                const p2 = digits.slice(4, 7);
                const p3 = digits.slice(7, 9);
                const p4 = digits.slice(9, 11);

                let formatted = '+7';
                if (p1) {
                    formatted += ` (${p1}`;
                    if (p1.length === 3) {
                        formatted += ')';
                    }
                }
                if (p2) {
                    formatted += p1 && p1.length === 3 ? ` ${p2}` : ` ${p2}`;
                }
                if (p3) {
                    formatted += `-${p3}`;
                }
                if (p4) {
                    formatted += `-${p4}`;
                }
                setLocalValue(formatted);
            }
        }
    };

    const handleLocalChange = (e) => {
        let next = e.target.value;
        const hasDateMask = className && className.includes('date-mask');
        const hasDeptMask = className && className.includes('dept-code-mask');
        const hasPassportSerialMask = className && className.includes('passport-serial-mask');
        const hasPassportNumberMask = className && className.includes('passport-number-mask');
        const hasPhoneMask = className && className.includes('phone-mask');

        // Простая маска для даты формата ДД.ММ.ГГГГ, если поле помечено классом date-mask
        if (hasDateMask) {
            // Оставляем только цифры
            const digits = next.replace(/\D/g, '').slice(0, 8);
            const parts = [];
            if (digits.length > 0) parts.push(digits.slice(0, 2));
            if (digits.length > 2) parts.push(digits.slice(2, 4));
            if (digits.length > 4) parts.push(digits.slice(4, 8));
            next = parts.join('.');
        }
        // Маска для кода подразделения: ХХХ-ХХХ, только цифры, максимум 6
        else if (hasDeptMask) {
            const digits = next.replace(/\D/g, '').slice(0, 6);
            const part1 = digits.slice(0, 3);
            const part2 = digits.slice(3, 6);
            next = part2 ? `${part1}-${part2}` : part1;
        }
        // Серия паспорта: только 4 цифры
        else if (hasPassportSerialMask) {
            next = next.replace(/\D/g, '').slice(0, 4);
        }
        // Номер паспорта: только 6 цифр
        else if (hasPassportNumberMask) {
            next = next.replace(/\D/g, '').slice(0, 6);
        }
        // Телефон: формат +7 (XXX) XXX-XX-XX, максимум 11 цифр
        else if (hasPhoneMask) {
            let digits = next.replace(/\D/g, '');
            if (!digits) {
                next = '';
            } else {
                if (digits.startsWith('8')) {
                    digits = '7' + digits.slice(1);
                } else if (!digits.startsWith('7')) {
                    digits = '7' + digits;
                }
                digits = digits.slice(0, 11);

                const p1 = digits.slice(1, 4);
                const p2 = digits.slice(4, 7);
                const p3 = digits.slice(7, 9);
                const p4 = digits.slice(9, 11);

                let formatted = '+7';
                if (p1) {
                    formatted += ` (${p1}`;
                    if (p1.length === 3) {
                        formatted += ')';
                    }
                }
                if (p2) {
                    formatted += p1 && p1.length === 3 ? ` ${p2}` : ` ${p2}`;
                }
                if (p3) {
                    formatted += `-${p3}`;
                }
                if (p4) {
                    formatted += `-${p4}`;
                }
                next = formatted;
            }
        }
        // Для полей типа number с integerOnly разрешаем только целые числа (без десятичных знаков)
        else if (type === 'number' && integerOnly) {
            // Удаляем все символы кроме цифр (точки, запятые, минусы и т.д.)
            next = next.replace(/[^\d]/g, '');
            // Если пусто, оставляем пустым
            if (next === '') {
                setLocalValue('');
                return;
            }
            // Преобразуем в число и обратно в строку, чтобы убрать ведущие нули
            const numValue = parseInt(next, 10);
            if (!isNaN(numValue)) {
                next = numValue.toString();
            } else {
                next = '';
            }
        }
        setLocalValue(next);
    };

    const handleBlur = () => {
        if (localValue !== value) {
            onChange(localValue);
        }
    };

    const isFilled = localValue && localValue.toString().trim() !== '';
    
    // ✨ Новая логика состояний:
    // 1. Если заполнено -> is-filled (зеленый)
    // 2. Если пусто и isRequired и showValidation -> is-validation-error (красный при попытке скачать)
    // 3. Если пусто и isRequired (но не showValidation) -> is-pending (фиолетовый, ожидает заполнения)
    // 4. Если не обязательное -> без дополнительных классов
    let stateClass = '';
    if (isFilled) {
        stateClass = 'is-filled';
    } else if (isRequired) {
        stateClass = showValidation ? 'is-validation-error' : 'is-pending';
    }
    
    const inputClassName = `inline-input ${className} ${stateClass}`;
    
    // Для поля даты мы будем использовать атрибут data-* для плейсхолдера в CSS
    const dataAttributes = type === 'date' ? { 'data-placeholder': placeholder } : {};
    
    // Текст для измерения: либо значение, либо плейсхолдер
    const textToMeasure = localValue || placeholder || ' ';

    // Для полей с integerOnly добавляем step="1" чтобы запретить ввод десятичных чисел
    // Для остальных полей number (площадь) разрешаем десятичные числа
    const numberAttributes = type === 'number' && integerOnly ? { step: '1', min: '0' } : {};

    return (
        <span className="editable">
            <input
                type={type} // ✨ Тип теперь всегда тот, что пришел в пропсах
                value={localValue || ''}
                onChange={handleLocalChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onBlur={handleBlur}
                placeholder={placeholder}
                className={inputClassName}
                style={{
                    width: fullWidth ? '100%' : `${inputWidth}px`,
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    ...style
                }}
                required={isRequired} // ✨ Добавляем нативный required для работы :invalid
                {...(validationFieldId ? { 'data-validation-field': validationFieldId } : {})}
                {...dataAttributes}
                {...numberAttributes}
            />
            <span ref={spanRef} className="measurement-span">{textToMeasure}</span>
        </span>
    );
};

export default InteractiveField;